import { fetchSubmodel, createSubmodelElement, patchSubmodelElementValue } from '../../services/DppManagerService';
import { uploadProblemReportImages } from '../../services/FilebrowserService';
import { serviceRequestNotification, createAttachedMediaCollection, createMediaCollection } from '../../sm-templates/ServiceRequestNotifications';

// Helper: get highest ServiceRequestNotification number
const getHighestNotificationNumber = (submodelElements) => {
  if (!Array.isArray(submodelElements)) return 0;

  const numbers = submodelElements
    .filter(item => item.idShort?.startsWith('ServiceRequestNotification'))
    .map(item => {
      const match = item.idShort.match(/ServiceRequestNotification(\d+)/);
      return match ? parseInt(match[1], 10) : 0;
    })
    .filter(n => !isNaN(n));

  return numbers.length > 0 ? Math.max(...numbers) : 0;
};

const countServiceRequestNotifications = (submodelElements = []) => {
  if (!Array.isArray(submodelElements)) return 0;

  return submodelElements.filter(item =>
    typeof item.idShort === 'string' &&
    item.idShort.startsWith('ServiceRequestNotification') &&
    /^\d+$/.test(item.idShort.replace('ServiceRequestNotification', ''))
  ).length;
};

const buildNotificationPayloadWithUrls = (template, data, assetInformation, newNumber, uploadedMedia = []) => {
  const payload = JSON.parse(JSON.stringify(template));
  payload.idShort = `ServiceRequestNotification${newNumber}`;

  payload.displayName = [
    { language: 'en', text: `Service Request Notification ${newNumber}` },
    { language: 'de', text: `Servicebedarfsmeldung ${newNumber}` }
  ];

  const serialNumber = assetInformation?.specificAssetIds?.[0]?.value || 'UNKNOWN';

  let insideReportedByContact = false;

  // Prepare AttachedMedia structure
  const attachedMediaFiles = data.AttachedMedia && Array.isArray(data.AttachedMedia)
    ? data.AttachedMedia
    : [];

  const attachedMediaCollection = createAttachedMediaCollection(uploadedMedia);

  uploadedMedia.forEach((uploadedFile, index) => {
    const mediaIndex = index + 1;
    const originalFile = attachedMediaFiles.find(f => f.name === uploadedFile.filename) || {};

    const mediaCollection = createMediaCollection(mediaIndex, uploadedFile, originalFile);

    attachedMediaCollection.value.push(mediaCollection);
  });

  const fill = (elements) => {
    if (!Array.isArray(elements)) return;

    for (const el of elements) {

      if (el.idShort === 'ReportedBy' && el.modelType === 'SubmodelElementCollection') {
        insideReportedByContact = true;
      }
      if (el.idShort === 'OnsiteContact' || el.idShort === 'DetailedInformation') {
        insideReportedByContact = false;
      }

      if (insideReportedByContact && el.idShort === 'ContactInformation' && el.modelType === 'SubmodelElementCollection') {
        el.value?.forEach(contactEl => {
          if (contactEl.idShort === 'Company' && contactEl.modelType === 'MultiLanguageProperty') {
            contactEl.value = data.Company ? [{ language: 'de', text: data.Company }] : [];
          }
          if (contactEl.idShort === 'FirstName' && contactEl.modelType === 'MultiLanguageProperty') {
            contactEl.value = data.FirstName ? [{ language: 'de', text: data.FirstName }] : [];
          }
          if (contactEl.idShort === 'NameOfContact' && contactEl.modelType === 'MultiLanguageProperty') {
            const full = `${data.LastName || ''}`.trim();
            contactEl.value = full ? [{ language: 'de', text: full }] : [];
          }
          if (contactEl.idShort === 'CityTown' && contactEl.modelType === 'MultiLanguageProperty') {
            contactEl.value = data.CityTown ? [{ language: 'de', text: data.CityTown }] : [];
          }
          if (contactEl.idShort === 'Street' && contactEl.modelType === 'MultiLanguageProperty') {
            contactEl.value = data.Street ? [{ language: 'de', text: data.Street }] : [];
          }
          if (contactEl.idShort === 'Zipcode' && contactEl.modelType === 'MultiLanguageProperty') {
            contactEl.value = data.Zipcode ? [{ language: 'de', text: data.Zipcode }] : [];
          }
          if (contactEl.idShort === 'StateCounty' && contactEl.modelType === 'MultiLanguageProperty') {
            contactEl.value = data.StateCounty ? [{ language: 'de', text: data.StateCounty }] : [];
          }
          if (contactEl.idShort === 'NationalCode' && contactEl.modelType === 'MultiLanguageProperty') {
            contactEl.value = data.NationalCode ? [{ language: 'de', text: data.NationalCode }] : [];
          }

          // Email: inside sub-collection with idShort "Email"
          if (contactEl.idShort === 'Email' && contactEl.modelType === 'SubmodelElementCollection') {
            contactEl.value?.forEach(emailEl => {
              if (emailEl.idShort === 'EmailAddress' && emailEl.modelType === 'Property') {
                emailEl.value = data.Email || '';
              }
            });
          }

          // Phone: inside sub-collection with idShort "Phone"
          if (contactEl.idShort === 'Phone' && contactEl.modelType === 'SubmodelElementCollection') {
            contactEl.value?.forEach(phoneEl => {
              if (phoneEl.idShort === 'TelephoneNumber' && phoneEl.modelType === 'MultiLanguageProperty') {
                phoneEl.value = data.Phone ? [{ language: 'de', text: data.Phone }] : [];
              }
            });
          }
        });
      }

      if (el.modelType === 'Property') {
        switch (el.idShort) {
          case 'CustomerNumber':
            el.value = data.CustomerNumber || '';
            break;
          case 'ServiceRequestNotificationId':
            el.value = `SRN-${serialNumber}-${String(newNumber).padStart(5, '0')}`;
            break;
          case 'Priority':
            el.value = data.Priority || '';
            break;
          case 'Status':
            el.value = 'sent';
            break;
          case 'ServiceType':
            el.value = data.ServiceType || '';
            break;
          case 'ErrorCode':
            el.value = data.ErrorCode || '';
            break;
          case 'StartOfFault':
            el.value = data.StartOfFault || new Date().toISOString().split('T')[0];
            break;
        }
      }

      if (el.modelType === 'Entity') {
        switch (el.idShort) {
          case 'RelatedAsset':
            el.globalAssetId = assetInformation?.globalAssetId || '';
            el.specificAssetIds = assetInformation?.specificAssetIds || []
            break;
          case 'SenderSystem':
            el.globalAssetId = assetInformation?.globalAssetId || '';
            el.specificAssetIds = assetInformation?.specificAssetIds || []
            break;
        }
      }

      if (el.modelType === 'MultiLanguageProperty') {
        if (el.idShort === 'ShortText') {
          el.value = [{ language: 'de', text: data.ShortText || '' }];
        }
        if (el.idShort === 'LongText') {
          el.value = [{ language: 'de', text: data.LongText || '' }];
        }
      }

      if (el.idShort === 'AttachedMedia' && el.modelType === 'SubmodelElementCollection') {
        el.value = attachedMediaCollection.value;
      }

      // Recurse deeper
      if (el.value && Array.isArray(el.value)) {
        fill(el.value);
      }
    }
  };

  fill(payload.value);
  return payload;
};

const ReportProblemHandler = async ({ data, setError, setSuccess, smId, assetInformation }) => {
  try {
    if (!smId || !data) {
      throw new Error('Submodel ID and form data are required');
    }

    const submodel = await fetchSubmodel(smId);

    const highest = getHighestNotificationNumber(submodel?.submodelElements);
    const newNumber = highest + 1;
    const count = countServiceRequestNotifications(submodel?.submodelElements);
    const newCount = count + 1;

    let uploadedMedia = [];
    const attachedMediaFiles = data.AttachedMedia && Array.isArray(data.AttachedMedia)
      ? data.AttachedMedia
      : [];

    console.log(attachedMediaFiles);

    if (attachedMediaFiles.length > 0) {
      // Generate folder id/name for image upload using serialNumber
      const folderId = assetInformation?.specificAssetIds?.find(id => id.key === 'serialNumber')?.value
                     || assetInformation?.specificAssetIds?.[0]?.value
                     || 'unknown';

      const uploadResults = await uploadProblemReportImages(attachedMediaFiles, folderId);

      // Check for failures
      const failedUploads = uploadResults.filter(r => !r.success);
      if (failedUploads.length > 0) {
        throw new Error(
          `Upload failed for: ${failedUploads.map(f => f.filename).join(', ')}`
        );
      }

      uploadedMedia = uploadResults; // contains { filename, url, success }
    }

    const payload = buildNotificationPayloadWithUrls(
      serviceRequestNotification,
      data,
      assetInformation,
      newNumber,
      uploadedMedia
    );

    await createSubmodelElement(smId, payload);
    await patchSubmodelElementValue(smId, "NumberOfServiceRequestNotifications", newCount);

    const successMessage = (
      <>
        {'Aktion erfolgreich.'}{' '}
        <a className="alert-link" href="#" onClick={() => window.location.reload()}>
          {'Seite neu laden um Änderungen zu sehen'}
        </a>
      </>
    );
    setSuccess(successMessage);

  } catch (err) {
    console.error('Failed to create ServiceRequestNotification:', err);
    setError(err.message || 'Fehler beim Erstellen der Serviceanfrage.');
  }
};

export default ReportProblemHandler;